import { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { X, MagnifyingGlass, CheckCircle, ListBullets, Spinner, Info } from "@phosphor-icons/react";

/**
 * V4.18 — Modale d'audit d'un KPI du Pilotage.
 *
 * Permet d'auditer dossier par dossier ce qui compose un chiffre.
 * Pour l'instant : KPI 'mandats_realises' (mandat_signe=True ET mandat_signe_date ∈ mois courant).
 *
 * Affiche : nom, date de signature, étape, CA net.
 * Source explicitement labellisée pour transparence.
 */

const STAGE_LABELS = {
  prospect: "Prospect",
  compromis: "Compromis",
  envoi_banque: "Envoi banque",
  accord_banque: "Accord banque",
  accord_risque: "Accord risque",
  rdv_bancaire: "RDV bancaire",
  offre_editee: "Offre éditée",
  acte_authentique: "Acte authentique",
};

function fmtDateFr(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function KpiAuditModal({ open, onClose, kpi = "mandats_realises", title = "Audit KPI", onOpenDossier }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get(`/pilotage/objectif/audit?kpi=${encodeURIComponent(kpi)}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, kpi]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="kpi-audit-overlay"
    >
      <div
        className="bg-white w-full md:max-w-2xl md:rounded-2xl rounded-t-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="kpi-audit-modal"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[color:var(--cf-primary-soft)] flex items-center justify-center flex-shrink-0">
            <ListBullets size={18} weight="fill" color="var(--cf-primary-hover)" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline text-[color:var(--cf-primary-hover)] mb-0.5">Audit · Mode transparence</div>
            <h3 className="font-display text-lg font-extrabold text-slate-900">{title}</h3>
            {data && (
              <div className="text-[11.5px] text-slate-500 font-semibold mt-1 leading-snug">
                <strong>{data.count}</strong> dossier{data.count > 1 ? "s" : ""} ·{" "}
                Période <strong>{fmtDateFr(data.period.start)} → {fmtDateFr(data.period.end)}</strong>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 transition-colors"
            data-testid="kpi-audit-close"
            aria-label="Fermer"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Bandeau source */}
        {data && (
          <div className="px-5 py-2 bg-[color:var(--cf-primary-soft)] border-b border-[color:var(--cf-primary-soft)] text-[11px] font-semibold text-[color:var(--cf-primary-hover)] flex items-center gap-1.5">
            <Info size={11} weight="bold" />
            <span>
              Source unique : <code className="font-mono bg-white/70 px-1 py-0.5 rounded">{data.source}</code>{" "}
              — un dossier compte uniquement si <code className="font-mono bg-white/70 px-1 py-0.5 rounded">mandat_signe=true</code>
              {" "}ET sa date de signature est dans la période.
            </span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && (
            <div className="space-y-2" data-testid="kpi-audit-loading">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-slate-50 rounded-lg animate-pulse" />
              ))}
            </div>
          )}
          {!loading && data?.count === 0 && (
            <div className="py-10 text-center" data-testid="kpi-audit-empty">
              <CheckCircle size={32} weight="fill" color="var(--cf-text-3)" className="mx-auto mb-2 opacity-50" />
              <div className="font-bold text-slate-600">Aucun dossier ne compose ce chiffre</div>
              <div className="text-[12px] text-slate-400 font-semibold mt-1">
                Aucun mandat signé sur la période avec une date renseignée.
              </div>
            </div>
          )}
          {!loading && data?.items?.length > 0 && (
            <ul className="space-y-1.5" data-testid="kpi-audit-list">
              {data.items.map((it, idx) => (
                <li
                  key={it.id || idx}
                  onClick={() => onOpenDossier && onOpenDossier(it)}
                  className={`rounded-xl p-3 border border-slate-100 flex items-center gap-3 ${
                    onOpenDossier ? "cursor-pointer hover:bg-slate-50" : ""
                  } transition-colors`}
                  data-testid={`kpi-audit-row-${it.id}`}
                >
                  {/* Index */}
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-[12px] font-extrabold tabular-nums text-slate-500">{idx + 1}</span>
                  </div>
                  {/* Nom + étape */}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[14px] text-slate-900 truncate">
                      {it.nom || it.client_name || "(sans nom)"}
                    </div>
                    <div className="text-[11px] text-slate-500 font-semibold">
                      {STAGE_LABELS[it.etape] || it.etape || "—"}
                    </div>
                  </div>
                  {/* Date + CA */}
                  <div className="text-right flex-shrink-0">
                    <div className="font-extrabold text-[12.5px] text-slate-900 tabular-nums">
                      {fmtDateFr(it.mandat_signe_date)}
                    </div>
                    <div className="text-[11px] text-slate-500 font-semibold tabular-nums">
                      {formatEUR(it.ca_net)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
